import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Chip,
  Alert,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { AnalyticsOutlined } from '@mui/icons-material';
import { ApiError, apiGet, apiPost, showErrorToast } from '../../services/apiClient';
import { ScannedEmailFields, EmailSource } from '../../models/mail';
import { IMAPSource } from '../../models/email-service';
import { AuthContext } from '../../context/authContext';
import { TenantContext } from '../../context/tenantContext';
import { toast } from '../../utils/toast';
import StandardDialog from '../../components/dialogs/base/standardDialog';
import OfferReviewDialog from './components/offerReviewDialog';
import MailDetailsView from './components/mailDetailsView';
import { MailOfferReview, MailOfferReviewStatus, OfferReview, ScannedMail } from '../../models/mailOfferReview';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiEndpoints';

function isImapConfigured(opts: { imapSource?: IMAPSource; emailAccessToken?: string | null; imapPassword?: string | null } | null | undefined): boolean {
  if (!opts) return false;
  if (opts.imapSource === IMAPSource.NONE || opts.imapSource === IMAPSource.ERRORRED) return false;
  if (opts.imapSource === IMAPSource.OFFICE365 || opts.imapSource === IMAPSource.GMAIL) return !!opts.emailAccessToken;
  return !!opts.imapPassword;
}

const MailScanOffersPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const authContext = useContext(AuthContext);
  const tenantContext = useContext(TenantContext);

  const [mails, setMails] = useState<ScannedMail[]>([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);
  const [selectedMail, setSelectedMail] = useState<ScannedMail | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [extractedOffers, setExtractedOffers] = useState<OfferReview[]>([]);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [analysisMessage, setAnalysisMessage] = useState<string | null>(null);
  const hasUserEmail = isImapConfigured(authContext?.user);
  const hasTenantEmail = isImapConfigured(tenantContext?.tenant);
  const bothConfigured = hasUserEmail && hasTenantEmail;

  const [emailSource, setEmailSource] = useState<EmailSource>('user');
  const effectiveSource: EmailSource = bothConfigured ? emailSource : (hasUserEmail ? 'user' : 'tenant');
  const [noEmailConfig, setNoEmailConfig] = useState(false);

  const fetchMails = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/emails/recent`, {
        count: 20,
        source: effectiveSource,
        with_status: true
      });
      const { emails } = await resp.json();
      setNoEmailConfig(false);
      setMails(emails || []);
      setRowCount(emails?.length || 0);
    } catch (error) {
      if (error instanceof ApiError && error.errorData?.error?.additionalInfo === 'client.extended.noEmailConfig') {
        setNoEmailConfig(true);
      } else {
        showErrorToast(error, t('Failed to fetch mails'));
      }
    } finally {
      setLoading(false);
    }
  }, [t, effectiveSource]);

  useEffect(() => {
    fetchMails();
  }, [effectiveSource]);

  // Open details dialog if messageId is in URL params
  useEffect(() => {
    const messageId = searchParams.get('messageId');
    if (messageId && mails.length > 0) {
      const mail = mails.find((m) => m.messageId === messageId);
      if (mail) {
        if (mail.mailOfferReviewStatus === MailOfferReviewStatus.REVIEW) {
          setSelectedMail(mail);
          (async () => {
            try {
              setAnalyzing(true);
              const offers = await fetchOfferReviewsForMail(mail);
              setExtractedOffers(offers.offerReviews);
              setShowReviewDialog(true);
            } catch (error) {
              showErrorToast(error, t('Failed to load offer reviews'));
            } finally {
              setAnalyzing(false);
            }
          })();
        } else {
          setSelectedMail(mail);
          setDetailsOpen(true);
        }
      }
    }
  }, [searchParams, mails, t]);

  const handleAnalyzeMail = async (mail: ScannedMail) => {
    try {
      setAnalyzing(true);
      setAnalysisMessage(null);
      const resp = await apiPost(
        `${process.env.REACT_APP_API_URL}/api/v1/offers/analyze`,
        {
          source: 'OfferQueryResponse',
          contact_email: mail.from,
          email_body: mail.plainPart,
          message_id: mail.messageId,
          attached_pdfs: mail.attachedPdfs || [],
          attached_xlsx: mail.attachedXlsx || [],
          attached_csv: mail.attachedCsv || [],
          inline_images: mail.inlineImages || [],
          dry_run: true,
        }
      );
      const offers: { result: OfferReview[] } = await resp.json();
      const extractedOffersList = offers.result || [];
      
      if (extractedOffersList.length === 0) {
        setAnalysisMessage(t('No offers extracted. Please verify that this mail contains relevant offer information.'));
        setExtractedOffers([]);
      } else {
        setExtractedOffers(extractedOffersList);
        setShowReviewDialog(true);
        toast.success(t('Mail analyzed successfully. Review and correct offers before creating.'));
      }
      await fetchMails();
    } catch (error) {
      showErrorToast(error, t('Failed to analyze mail'));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleCreateOfferFromMail = async () => {
    try {
      setAnalyzing(true);
      const resp = await apiPost(
        `${process.env.REACT_APP_API_URL}/api/v1/offers/create`,
        {
          source: 'Manual',
          offers: extractedOffers,
          message_id: selectedMail?.messageId,
        }
      );
      await resp.json();
      toast.success(t('Offers created successfully'));
      await fetchMails();
      setShowReviewDialog(false);
      handleCloseDetails();
      setExtractedOffers([]);
    } catch (error) {
      showErrorToast(error, t('Failed to create offers'));
    } finally {
      setAnalyzing(false);
    }
  };

  const fetchOfferReviewsForMail = async (mail: ScannedMail): Promise<MailOfferReview> => {
    const url = buildApiUrl(API_ENDPOINTS.offers.reviewsByMessageId(mail.messageId));
    const resp = await apiGet(url);
    const data: { result: MailOfferReview } = await resp.json();
    return data.result;
  };

  const handleRowClick = async (params: GridRowParams): Promise<void> => {
    const mail = mails.find((m) => m.messageId === params.id);
    if (!mail) return;

    if (mail.mailOfferReviewStatus === MailOfferReviewStatus.REVIEW) {
      setSelectedMail(mail);
      try {
        setAnalyzing(true);
        const offers = await fetchOfferReviewsForMail(mail);
        setExtractedOffers(offers.offerReviews);
        setShowReviewDialog(true);
      } catch (error) {
        showErrorToast(error, t('Failed to load offer reviews'));
      } finally {
        setAnalyzing(false);
      }
    } else {
      setSelectedMail(mail);
      setDetailsOpen(true);
    }
  };

  const handleCloseDetails = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('messageId');
      return params;
    });
    setDetailsOpen(false);
    setAnalysisMessage(null);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: ScannedEmailFields.from,
        headerName: t('From'),
        flex: 1,
        minWidth: 150,
      },
      {
        field: ScannedEmailFields.messageSubject,
        headerName: t('Subject'),
        flex: 1.5,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams) => params.value || '(no subject)',
      },
      {
        field: 'attachments',
        headerName: t('Attachments'),
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as ScannedMail;
          const attachmentTypes = [
            { key: 'inlineImages', label: t('Images') },
            { key: 'attachedPdfs', label: t('PDF') },
            { key: 'attachedXlsx', label: t('Excel') },
            { key: 'attachedCsv', label: t('CSV') },
          ];

          const presentAttachments = attachmentTypes.filter(
            (type) => row[type.key as keyof ScannedMail] !== undefined && (row[type.key as keyof ScannedMail] as string[]).length > 0
          );

          if (presentAttachments.length === 0) {
            return <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>;
          }

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                {presentAttachments.map((type) => (
                  <Chip key={type.key} label={type.label} size="small" variant="outlined" />
                ))}
              </Stack>
            </Box>
          );
        },
      },
      {
        field: ScannedEmailFields.mailOfferReviewStatus,
        headerName: t('Status'),
        flex: 0.8,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams) => {
          const status = params.row.mailOfferReviewStatus as MailOfferReviewStatus;
          const getStatusDisplay = () => {
            switch (status) {
              case MailOfferReviewStatus.CREATED:
                return {
                  label: t('Created'),
                  color: 'success' as const,
                  description: t('Offers have been successfully created'),
                };
              case MailOfferReviewStatus.REVIEW:
                return {
                  label: t('Review'),
                  color: 'warning' as const,
                  description: t('Ready for your review, click to check and review offers before creation'),
                };
              case MailOfferReviewStatus.DETECTED:
                return {
                  label: t('Detected'),
                  color: 'info' as const,
                  description: t('Offers detected, click to extract them and review before creation'),
                };
              case MailOfferReviewStatus.EMPTY:
                return {
                  label: t('Empty'),
                  color: 'default' as const,
                  description: t('No offers found in this email'),
                };
              case MailOfferReviewStatus.PENDING:
              default:
                return {
                  label: t('Pending'),
                  color: 'default' as const,
                  description: t('Click to analyze this email for offers'),
                };
            }
          };

          const statusDisplay = getStatusDisplay();
          return (
            <Tooltip title={statusDisplay.description}>
              <Chip label={statusDisplay.label} color={statusDisplay.color} size="small" />
            </Tooltip>
          );
        },
      },
    ],
    [t]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {t('Mail Offer Scanning')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {t('Select any email to analyze with AI and create new offers')}
          </Typography>
        </Box>
        {bothConfigured && (
          <Box sx-={{ display: 'flex', alignItems: 'flex-end'}}>
            <FormControlLabel sx={{ display: 'flex', alignItems: 'flex-end'}}
              control={
                <Switch
                  disabled={loading}
                  checked={emailSource === 'tenant'}
                  onChange={(e) => setEmailSource(e.target.checked ? 'tenant' : 'user')}
                />
              }
              label={
                <Box>
                  <Typography component="span">{ emailSource === 'tenant' ? t('Tenant') : t('User') }</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('Working email source for scanning offers')}
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}
      </Stack>

      {noEmailConfig && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('Email service is not configured. Please set up your email integration in account settings to use this feature.')}
        </Alert>
      )}

      <Box
        sx={{
          height: 600,
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'border.light',
        }}
      >
        <DataGrid
          rows={mails}
          columns={columns}
          getRowId={(row) => row.messageId}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20]}
          rowCount={rowCount}
          loading={loading}
          paginationMode="client"
          onRowClick={handleRowClick}
          sx={{
            '& .MuiDataGrid-cell': {
              cursor: 'pointer',
            },
          }}
        />
      </Box>

      {/* Details Dialog */}
      <StandardDialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        title={t('Mail Details')}
        subtitle={t('View and analyze the selected email to extract offers')}
        width={{ xs: '95%', sm: '85%', md: '650px', lg: '750px' }}
        autoSize={true}
        primaryAction={
          selectedMail && selectedMail.mailOfferReviewStatus !== MailOfferReviewStatus.CREATED
            ? {
              label: t('Extract Offers'),
              onClick: () => handleAnalyzeMail(selectedMail),
              disabled: analyzing,
              loading: analyzing,
              startIcon: <AnalyticsOutlined />,
            }
            : undefined
        }
        secondaryAction={{
          label: t('Close'),
          onClick: handleCloseDetails,
          disabled: analyzing,
        }}
        contentSx={{ p: { xs: 2, sm: 2.5, md: 3 } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {analysisMessage && (
            <Alert severity="info">
              {analysisMessage}
            </Alert>
          )}
          {selectedMail && (
            <MailDetailsView
              mail={{
                from: selectedMail.from,
                subject: selectedMail.messageSubject,
                body: selectedMail.plainPart || '',
                messageId: selectedMail.messageId,
                inReplyTo: selectedMail.inReplyTo,
              }}
              showMessageId
            />
          )}
        </Box>
      </StandardDialog>

      {/* Review & Correct Offers Dialog */}
      <OfferReviewDialog
        open={showReviewDialog}
        offers={extractedOffers}
        analyzing={analyzing}
        mailDetails={
          selectedMail
            ? {
              from: selectedMail.from,
              subject: selectedMail.messageSubject || '(no subject)',
              body: selectedMail.plainPart || '',
            }
            : undefined
        }
        mailStatus={selectedMail?.mailOfferReviewStatus}
        onClose={() => setShowReviewDialog(false)}
        onCreate={handleCreateOfferFromMail}
        onOffersChange={setExtractedOffers}
      />
    </Box>
  );
};

export default MailScanOffersPage;
